/**
 * Shared reader for computer-use ``desktop_required`` tool errors.
 * BE puts ``{ code, reason, trigger }`` on result_provider_metadata.data.
 */

import type {
  ComputerUseComposerBannerKind,
  DesktopRequiredPayload,
  DesktopRequiredReason,
  DesktopRequiredScanItem,
  DesktopRequiredTrigger,
  OnDemandDesktopRequiredMiss,
} from '../../../contracts/desktop-required.types';
import { isRecord } from '../../../contracts/parse-utils';
import { RUN_COMPUTER_USE_TOOL_NAME } from '../../harness/timeline/derive-is-computer-use';

const isDesktopRequiredTrigger = (value: unknown): value is DesktopRequiredTrigger =>
  value === 'on_demand' || value === 'schedule' || value === 'event';

const isDesktopRequiredReason = (value: unknown): value is DesktopRequiredReason =>
  value === 'no_target_online' || value === 'no_target_selected';

const readToolData = (
  item: DesktopRequiredScanItem
): Record<string, unknown> | undefined => {
  const data = item.result_provider_metadata?.data;
  return isRecord(data) ? data : undefined;
};

export const isDesktopRequiredData = (
  data: Record<string, unknown> | undefined
): data is Record<string, unknown> & { code: 'desktop_required' } =>
  isRecord(data) && data.code === 'desktop_required';

export const readDesktopRequired = (
  item: DesktopRequiredScanItem
): DesktopRequiredPayload | null => {
  const data = readToolData(item);
  if (!isDesktopRequiredData(data)) return null;
  return {
    trigger: isDesktopRequiredTrigger(data.trigger) ? data.trigger : 'on_demand',
    reason: isDesktopRequiredReason(data.reason) ? data.reason : 'no_target_online',
  };
};

/** Teach on web; retry on desktop; hide after dismiss or a later CU attempt. */
export const resolveComputerUseComposerBanner = ({
  missId,
  dismissedId,
  isDesktopAvailable,
}: {
  missId: string | null;
  dismissedId: string | null;
  isDesktopAvailable: boolean;
}): ComputerUseComposerBannerKind => {
  if (!missId || missId === dismissedId) return 'none';
  return isDesktopAvailable ? 'retry' : 'teach';
};

/** Model-facing continue prompt for a new turn — not a resume of the failed tool. */
export const buildComputerUseRetryPrompt = (task?: string): string => {
  const trimmed = task?.trim();
  if (trimmed) return `Continue the computer use task: ${trimmed}`;
  return 'Continue the computer use task from this desktop.';
};

const TASK_KEYS = ['raw_task', 'task', 'goal', 'prompt', 'message'] as const;

const readScanTask = (input?: Record<string, unknown>): string | undefined => {
  if (!input) return undefined;
  for (const key of TASK_KEYS) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
};

const isComputerUseScanItem = (item: DesktopRequiredScanItem): boolean =>
  item.isComputerUse === true || item.name === RUN_COMPUTER_USE_TOOL_NAME;

const RECORD_COMPUTER_USE_SKILL_TOOL_NAME = 'record_computer_use_skill';

/** Latest in-chat miss the composer nudge can still recover from. */
export const findLatestOnDemandDesktopRequired = (
  items: readonly DesktopRequiredScanItem[]
): OnDemandDesktopRequiredMiss | null => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (!item || item.kind !== 'tool_use' || typeof item.id !== 'string') continue;
    // Recording needs the clinician to act first, so a computer-use retry
    // cannot recover it. Keep scanning: an earlier computer-use miss still can.
    if (item.name === RECORD_COMPUTER_USE_SKILL_TOOL_NAME) continue;
    const payload = readDesktopRequired(item);
    if (payload?.trigger === 'on_demand') {
      const task = readScanTask(item.input);
      return task ? { id: item.id, payload, task } : { id: item.id, payload };
    }
    if (isComputerUseScanItem(item)) return null;
  }
  return null;
};
